<template>
  <div class="tw-h-screen tw-flex tw-flex-col tw-overflow-hidden tw-bg-gray-100" @keydown.escape="isSidebarOpen = false">
    <div class="tw-w-100 tw-bg-primary-100 tw-flex tw-justify-between tw-items-center tw-text-primary-600 tw-py-0.5 tw-px-4 tw-text-sm" v-if="isAuthenticatedInPinMode">
      <div />
      <div>You are in pin mode as {{ userName }}</div>
      <base-button size="xs" type="secondary" @click="enablePinMode">
        Switch User
        <template #icon>
          <sign-out-icon />
        </template>
      </base-button>
    </div>
    <div class="tw-flex tw-flex-grow tw-overflow-auto">
      <transition leave-active-class="tw-ease-in tw-duration-200">
        <div v-show="isSidebarOpen" class="md:tw-hidden" @click="isSidebarOpen = false">
          <transition enter-class="tw-opacity-0" enter-to-class="tw-opacity-100" leave-class="tw-opacity-100" leave-to-class="tw-opacity-0">
            <div
              @click="isSidebarOpen = false"
              v-show="isSidebarOpen"
              class="tw-fixed tw-inset-0 tw-z-30 tw-transition-opacity tw-ease-linear tw-duration-300 tw-backdrop-blur tw-backdrop-filter"
            >
              <div class="tw-absolute tw-inset-0 tw-bg-gray-600 tw-opacity-50"></div>
            </div>
          </transition>
          <div class="tw-fixed tw-inset-0 tw-flex tw-z-40">
            <transition enter-class="tw--translate-x-full" enter-to-class="tw-translate-x-0" leave-class="tw-translate-x-0" leave-to-class="tw--translate-x-full">
              <div v-show="isSidebarOpen" class="tw-flex-1 tw-flex tw-flex-col tw-max-w-xs tw-w-full tw-bg-gray-800 tw-transform tw-ease-in-out tw-duration-300">
                <div class="tw-absolute tw-top-0 tw-right-0 tw--mr-14 tw-p-1">
                  <base-icon class="tw-mt-2 tw-mr-2" v-show="isSidebarOpen" @click="isSidebarOpen = false">
                    <x-icon class="tw-h-6 tw-w-6 tw-text-white" />
                  </base-icon>
                </div>
                <div class="tw-flex-shrink-0 tw-flex tw-items-center tw-px-4 tw-py-3 tw-text-white tw-font-semibold tw-text-lg nav-bar">
                  <component :is="logo" class="tw-h-10" />
                </div>
                <div class="tw-mt-5 tw-flex-1 tw-h-0 tw-overflow-y-auto">
                  <nav class="tw-pb-6">
                    <router-link
                      v-for="navItem in navItems"
                      :key="navItem.path"
                      :to="navItem.path"
                      @click.native.prevent="navigateIfNotCurrent(navItem.path)"
                      :class="[
                        'hover:tw-no-underline tw-group tw-flex tw-items-center tw-px-4 tw-py-2 tw-text-sm tw-font-normal tw-mb-1 tw-relative tw-transition-colors tw-duration-100 tw-ease-out',
                        isActivePath(navItem) ? 'tw-bg-gray-900 tw-text-gray-100 hover:tw-text-gray-100' : 'tw-text-gray-400 hover:tw-bg-gray-700 hover:tw-text-gray-400',
                      ]"
                    >
                      <div v-if="isActivePath(navItem)" class="tw-absolute tw-top-0 tw-bottom-0 tw-left-0 tw-w-1 tw-bg-primary tw-rounded-r-md" />
                      <component
                        :is="navItem.icon"
                        :class="['tw-mr-4 tw-h-6 tw-w-6 tw-transition tw-ease-in-out tw-duration-150', isActivePath(navItem) ? 'tw-text-gray-200' : 'tw-text-gray-400']"
                      />
                      {{ navItem.text
                      }}<base-badge
                        v-if="navItem.text === 'Activities' && isLocationFiltered === true && todoCount > 0"
                        class="tw-ml-2 tw-relative tw--top-1"
                        :dark="true"
                        color="primary"
                        size="xs"
                      >
                        {{ todoCount }}
                      </base-badge>
                    </router-link>
                  </nav>
                </div>
              </div>
            </transition>
          </div>
        </div>
      </transition>
      <div class="tw-hidden md:tw-flex md:tw-flex-shrink-0 tw-bg-gray-800">
        <div class="tw-flex tw-flex-col tw-w-60">
          <div class="tw-flex tw-items-center tw-h-16 tw-flex-shrink-0 tw-p-4 tw-border-r tw-text-white nav-bar">
            <component :is="logo" class="tw-h-14 tw-py-2" />
          </div>
          <div class="tw-h-0 tw-flex-1 tw-flex tw-flex-col tw-overflow-y-auto">
            <nav class="tw-flex-1 tw-py-4 tw-bg-gray-800 tw-border-r">
              <router-link
                v-for="navItem in navItems"
                :key="navItem.path"
                :to="navItem.path"
                :class="[
                  'hover:tw-no-underline tw-group tw-flex tw-items-center tw-px-4 tw-py-2 tw-text-sm tw-font-normal tw-mb-1 tw-relative tw-transition-colors tw-duration-100 tw-ease-out',
                  isActivePath(navItem) ? 'tw-bg-gray-900 tw-text-gray-100 hover:tw-text-gray-100' : 'tw-text-gray-400 hover:tw-bg-gray-700 hover:tw-text-gray-400',
                ]"
              >
                <div v-if="isActivePath(navItem)" class="tw-absolute tw-top-0 tw-bottom-0 tw-left-0 tw-w-1 tw-bg-primary tw-rounded-r-md" />
                <component
                  :is="navItem.icon"
                  :class="['tw-mr-4 tw-h-6 tw-w-6 tw-transition tw-ease-in-out tw-duration-150', isActivePath(navItem) ? 'tw-text-gray-200' : 'tw-text-gray-400']"
                />
                {{ navItem.text
                }}<base-badge
                  v-if="navItem.text === 'Activities' && isLocationFiltered === true && todoCount > 0"
                  class="tw-ml-2 tw-relative tw--top-1"
                  :dark="true"
                  color="primary"
                  size="xs"
                >
                  {{ todoCount }}
                </base-badge>
              </router-link>
            </nav>
          </div>
        </div>
      </div>
      <div class="tw-flex tw-flex-col tw-w-0 tw-flex-1 tw-overflow-hidden">
        <div :class="['tw-relative tw-flex-shrink-0 tw-flex tw-h-16 tw-bg-white tw-z-20', { 'tw-shadow-sm': $route.meta.showNavShadow }]">
          <button
            @click.stop="isSidebarOpen = true"
            class="tw-px-4 tw-border-r tw-border-gray-200 tw-text-gray-500 focus:tw-outline-none focus:tw-bg-gray-100 focus:tw-text-gray-600 md:tw-hidden"
            v-if="!isOrganizationUser"
          >
            <menu-icon class="tw-w-6 tw-h-6" />
          </button>
          <div class="tw-flex-1 tw-flex tw-justify-between">
            <div class="tw-flex-1">
              <global-search v-if="isOrganizationBusiness" :business-id="organization.id" class="tw-hidden md:tw-block" />
            </div>
            <div class="tw-flex tw-items-center tw-px-4 sm:tw-px-6 lg:tw-px-8">
              <base-dropdown :options="profileOptions" align="right">
                <div class="tw-flex tw-items-center tw-cursor-pointer">
                  <div class="tw-h-10 tw-w-10">
                    <avatar
                      :business-id="isOrganizationBusiness ? organization.id : null"
                      :user-id="isOrganizationBusiness ? null : userId"
                      :avatar-type="isOrganizationBusiness ? 'business' : 'user'"
                    />
                  </div>
                  <div class="tw-ml-3 tw-leading-4">
                    <div class="tw-text-primary tw-text-sm">
                      {{ cleanUserName }}
                    </div>
                    <div class="tw-text-xs tw-text-gray-500">{{ organization.description }}</div>
                  </div>
                  <chevron-down-icon class="tw-h-5 tw-w-5 tw-ml-2 tw-text-gray-400" />
                </div>
                <template #option="{ option }">
                  <div class="tw-my-1 tw-w-full tw-border-b" v-if="option.isDivider" />
                  <button class="tw-flex tw-items-center tw-px-4 tw-py-2 tw-text-gray-600 focus:tw-outline-none" @click="option.onClick()" v-else>
                    <component :is="option.icon" class="tw-h-5 tw-w-5 tw-mr-2.5 tw-flex-shrink-0" />
                    <div class="tw-text-left">
                      <div :class="[{ 'tw-text-sm tw-leading-4': option.description }]">{{ option.title }}</div>
                      <div class="tw-text-xs tw-text-gray-500" v-if="option.description">{{ option.description }}</div>
                    </div>
                  </button>
                </template>
              </base-dropdown>
            </div>
          </div>
        </div>
        <div
          v-if="showActivationHeader"
          class="tw-flex tw-gap-2 tw-justify-between tw-border-t tw-border-b tw-bg-red-50 tw-text-red-600 tw-py-6 tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-text-base"
        >
          <div class="tw-flex-col">
            <div>Not Active</div>
            <div v-if="numRemainingActivationSteps > 3" class="tw-text-xs">
              Let's get started! Complete {{ numRemainingActivationSteps }} more steps to activate your business and get your {{ organization.customerLabelPlural }} signing up.
            </div>
            <div v-else class="tw-text-xs">
              You're almost there! Only {{ numRemainingActivationSteps }} more steps to activate your business and get your {{ organization.customerLabelPlural }} signing up.
            </div>
          </div>
          <base-button class="tw-my-auto tw-flex" type="primary" size="sm" @click="activation">
            <template v-slot:icon>
              <task-list-icon />
            </template>
            Checklist
          </base-button>
        </div>
        <main :class="['tw-flex-1 tw-relative tw-overflow-y-auto focus:tw-outline-none', { 'tw-pb-12': isOrganizationUser }]" tabindex="0">
          <slot />
        </main>
        <nav class="tw-absolute tw-inset-x-0 tw-bottom-0 tw-flex md:tw-hidden tw-bg-white tw-shadow-xl" v-if="isOrganizationUser">
          <router-link
            v-for="navItem in navItems"
            :key="navItem.path"
            :to="navItem.path"
            :class="[
              'hover:tw-no-underline tw-px-0 tw-py-2 tw-text-sm tw-font-normal tw-relative tw-transition-colors tw-duration-100 tw-ease-out tw-flex-1 tw-flex tw-flex-col tw-items-center',
              isActivePath(navItem) ? 'tw-text-primary hover:tw-text-primary' : 'tw-text-gray-500 hover:tw-text-gray-600',
            ]"
          >
            <div v-if="isActivePath(navItem)" class="tw-absolute tw-bottom-0 tw-w-20 tw-h-1 tw-bg-primary tw-rounded-t-md" />
            <component
              :is="navItem.icon"
              :class="['tw-h-6 tw-w-6 tw-transition tw-ease-in-out tw-duration-150 tw-mx-auto', isActivePath(navItem) ? 'tw-text-primary' : 'tw-text-gray-500']"
            />
            {{ navItem.shortText || navItem.text }}
          </router-link>
        </nav>
      </div>
    </div>
    <notification-wrapper />
    <global-error />
  </div>
</template>

<script>
import Avatar from "@/components/Avatar.vue";
import NotificationWrapper from "@/components/v2/NotificationWrapper";
import GlobalError from "@/components/v2/GlobalError";
import GlobalSearch from "@/components/v2/GlobalSearch";
import PredictileLogo from "@/assets/images/current_color_logo_horizontal.svg";
import RxHarmonyLogo from "@/assets/images/rx-harmony-logo.svg";
import PredictileColorLogo from "@/assets/images/predictile_logo_rgb.svg";
import {
  AlertIcon,
  MailIcon,
  MenuIcon,
  XIcon,
  ChevronDownIcon,
  UserCircleIcon,
  UserIcon,
  DocumentIcon,
  SignOutIcon,
  BusinessIcon,
  UsersIcon,
  CombineIcon,
  TestTubeIcon,
  GridIcon,
  SupportIcon,
  GraphIcon,
  PillIcon,
  CheckIcon,
  TrainingIcon,
  TableIcon,
  ReportIcon,
  SettingsIcon,
  LocationIcon,
  KeyIcon,
  TaskListIcon,
} from "@/assets/svgs";
import { mapGetters, mapActions, mapState } from "vuex";
import redirect from "@/mixins/redirect";
import { capitalize } from "lodash";

export default {
  mixins: [redirect],
  components: {
    Avatar,
    AlertIcon,
    MailIcon,
    NotificationWrapper,
    GlobalError,
    GlobalSearch,
    PredictileLogo,
    RxHarmonyLogo,
    PredictileColorLogo,
    MenuIcon,
    XIcon,
    ChevronDownIcon,
    UserCircleIcon,
    UserIcon,
    DocumentIcon,
    SignOutIcon,
    BusinessIcon,
    UsersIcon,
    CombineIcon,
    TestTubeIcon,
    GridIcon,
    SupportIcon,
    GraphIcon,
    PillIcon,
    CheckIcon,
    TrainingIcon,
    TableIcon,
    ReportIcon,
    SettingsIcon,
    LocationIcon,
    KeyIcon,
    TaskListIcon,
  },
  data() {
    return {
      isSidebarOpen: false,
      todoCount: 0,
      isLoadingCount: false,
      businessHasPins: false,
      todoInterval: null,
    };
  },
  computed: {
    ...mapGetters("cognito", { userId: "id", userName: "name", userEmail: "email" }),
    ...mapGetters("organization", [
      "organization",
      "organizations",
      "isOrganizationPredictile",
      "isOrganizationBusiness",
      "isOrganizationUser",
      "hasMultipleOrganizations",
      "cobrandId",
    ]),
    ...mapGetters("role", ["isPredictile", "isBusinessManagement", "isBusinessAdministrator", "isLocationFiltered", "isPscSecondaryRole"]),
    ...mapGetters("location", ["location", "locations"]),
    ...mapGetters("businessActivation", [
      "hasBusinessProfile",
      "hasProductsAndPlans",
      "hasBrandColorsAndLogo",
      "hasTaxRates",
      "hasLocation",
      "hasSubscription",
      "hasMerchantProcessing",
    ]),
    ...mapState("cognito", ["isAuthenticatedInPinMode"]),
    cleanUserName() {
      if (this.isOrganizationUser) return this.organization.customerLabelSingular !== null ? capitalize(this.organization.customerLabelSingular) + " Profile" : "Member Profile";
      else return this.userName.replace("Branch Development: ", "");
    },
    showActivationHeader() {
      return this.isOrganizationBusiness && !this.organization.isActive;
    },
    numRemainingActivationSteps() {
      let numRemaining = 0;
      if (!this.hasBusinessProfile) numRemaining++;
      if (!this.hasProductsAndPlans) numRemaining++;
      if (!this.hasBrandColorsAndLogo) numRemaining++;
      if (!this.hasTaxRates) numRemaining++;
      if (!this.hasLocation) numRemaining++;
      if (!this.hasSubscription) numRemaining++;
      if (!this.hasMerchantProcessing) numRemaining++;
      return numRemaining;
    },
    profileOptions() {
      const options = [];
      if (this.hasMultipleOrganizations || this.isPredictile) {
        options.push(
          ...this.organizations
            .filter((org) => org.id !== this.organization.id)
            .map((org) => ({
              title: org.description,
              description: this.getOrganizationDescriptor(org),
              icon: this.getOrganizationIcon(org),
              onClick: () => this.updateOrganization(org.id),
            })),
        );
        options.push({ isDivider: true });
      }
      if (this.canEnablePinMode) {
        options.push(
          {
            title: "Enable Pin Mode",
            icon: "key-icon",
            onClick: this.enablePinMode,
          },
          {
            isDivider: true,
          },
        );
      }
      options.push(
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        {
          title: "My Profile",
          icon: "user-icon",
          onClick: () => {
            const targetRoute = `/users/${this.userId}/profile/`;
            if (this.$route.path !== targetRoute) {
              this.$router.push(targetRoute);
            }
          },
        },
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        { title: "Sign Out", icon: "sign-out-icon", onClick: () => this.$router.push(`/signout${this.cobrandId ? `?cobrand=${this.cobrandId}` : ""}`) },
      );

      return options;
    },
    locationOptions() {
      return [...this.locations, { name: "All locations", isAll: true }];
    },
    navItems() {
      if (this.isOrganizationPredictile) {
        return this.predictileLinks;
      } else if (this.isOrganizationBusiness) {
        return this.businessLinks;
      } else if (this.isOrganizationUser) {
        return this.userLinks;
      }

      return [];
    },
    predictileLinks() {
      return [
        { text: "Businesses", icon: "business-icon", path: "/businesses" },
        { text: "Users", icon: "users-icon", path: "/admin/users" },
        { text: "Integrations", icon: "combine-icon", path: "/admin/integrations" },
        { text: "Services", icon: "test-tube-icon", path: "/admin/services" },
        { text: "Email Templates", icon: "mail-icon", path: "/admin/email" },
        { text: "Reconcile", icon: "report-icon", path: "/admin/subscription-reconcile" },
      ];
    },
    businessLinks() {
      const isBusinessManagement = this.isPredictile() || this.isBusinessManagement(this.organization.id);
      const isBusinessAdministrator = this.isPredictile() || this.isBusinessAdministrator(this.organization.id);

      return [
        { text: "Dashboard", icon: "graph-icon", path: `/businesses/${this.organization.id}/home` },
        {
          text: capitalize(this.organization.customerLabelPlural),
          icon: "users-icon",
          path: `/businesses/${this.organization.id}/members`,
        },
        ...(this.organization.usePrescriptionProgramSetting === true
          ? [{ text: "Prescriptions", icon: "pill-icon", path: `/businesses/${this.organization.id}/prescriptions` }]
          : []),
        ...(this.organization.useTodoSetting === true && this.isLocationFiltered === false
          ? [
              {
                text: "Activities",
                icon: "check-icon",
                path: `/businesses/${this.organization.id}/business-todo/unassigned`,
              },
            ]
          : []),
        ...(isBusinessAdministrator === true && this.isLocationFiltered === false
          ? [
              {
                text: "Disputes",
                icon: "alert-icon",
                path: `/businesses/${this.organization.id}/disputes`,
              },
            ]
          : []),
        ...(this.organization.useTodoSetting === true && this.isLocationFiltered === true
          ? [
              {
                text: "Activities",
                icon: "check-icon",
                path: `/businesses/${this.organization.id}/location-todo/${this.cobrandId === "rx-harmony" ? "follow-up" : "due-today"}`,
              },
            ]
          : []),
        { text: "Training", icon: "training-icon", path: `/businesses/${this.organization.id}/trainings/mytraining` },
        ...(!isBusinessManagement || this.isPscSecondaryRole || (this.isLocationFiltered && this.organization.operatingModel === "coordinator")
          ? []
          : [{ text: "Plans & Products", icon: "table-icon", path: `/businesses/${this.organization.id}/offerings` }]),
        ...(!this.isPscSecondaryRole ? [{ text: "Reports", icon: "report-icon", path: `/businesses/${this.organization.id}/reports` }] : []),
        { text: "Settings", icon: "settings-icon", path: `/businesses/${this.organization.id}/business` },
        ...(!this.isPscSecondaryRole ? [{ text: "Support", icon: "support-icon", path: `/businesses/${this.organization.id}/support` }] : []),
      ];
    },
    userLinks() {
      return [
        { text: "My Plans", shortText: "Plans", icon: "grid-icon", path: `/users/${this.organization.id}/plans` },
        { text: "My Documents", shortText: "Documents", icon: "document-icon", path: `/users/${this.organization.id}/documents` },
        { text: "My Profile", shortText: "Profile", icon: "user-icon", path: `/users/${this.organization.id}/profile` },
        { text: "Support", icon: "support-icon", path: "/user/support" },
      ];
    },
    logo() {
      switch (this.cobrandId) {
        case "rx-harmony":
          return "rx-harmony-logo";
      }
      return "predictile-logo";
    },
    canEnablePinMode() {
      return this.isOrganizationBusiness && this.businessHasPins;
    },
  },
  created() {
    this.loadTodoCount();

    setInterval(
      function() {
        this.loadTodoCount();
      }.bind(this),
      90000,
    );
    this.checkForBusinessPins();
    this.EventBus.$on("business-user-updated", this.checkForBusinessPins);
  },
  beforeDestroy() {
    this.EventBus.$off("business-user-updated", this.checkForBusinessPins);

    if (this.todoInterval) {
      clearInterval(this.todoInterval);
      this.todoInterval = null;
    }
  },

  watch: {
    // userId(newVal) {
    //   if (newVal) {
    //     this.loadTodoCount();
    //     this.startTodoInterval();
    //   }
    // },
    organization() {
      this.checkForBusinessPins();
    },
  },
  methods: {
    ...mapActions("organization", ["changeOrganization"]),
    ...mapActions("location", ["updateLocation"]),
    isActivePath(navItem) {
      if (navItem.text === "Activities" && this.isLocationFiltered === false)
        return this.$route.path.toLowerCase().startsWith("/businesses/" + this.organization.id + "/business-todo");
      else if (navItem.text === "Settings")
        return (
          this.$route.path.toLowerCase().startsWith(`/businesses/${this.organization.id}/business`) &&
          !this.$route.path.toLowerCase().startsWith(`/businesses/${this.organization.id}/business-todo`)
        );
      else return this.$route.path.toLowerCase().startsWith(navItem.path.toLowerCase());
    },
    navigateIfNotCurrent(path) {
      if (this.$route.path !== path) {
        this.$router.push(path);
      }
    },
    getOrganizationIcon(org) {
      switch (org.type) {
        case "predictile":
          return "predictile-color-logo";
        case "business":
          return "business-icon";
        case "user":
          return "user-circle-icon";
      }
    },
    getOrganizationDescriptor(org) {
      switch (org.type) {
        case "predictile":
          return "Predictile";
        case "business":
          return "Business User";
        case "user":
          return this.organization.customerLabelSingular !== null ? capitalize(this.organization.customerLabelSingular) + " Profile" : "Member Profile";
      }
    },
    async updateOrganization(id) {
      await this.changeOrganization(id);
      await this.redirectToHome();
    },
    startTodoInterval() {
      if (this.todoInterval) return;

      this.todoInterval = setInterval(() => {
        this.loadTodoCount();
      }, 90000);
    },
    loadTodoCount() {
      if (!this.userId) return;

      if (this.isLoadingCount === true || !this.organization || this.organization.useTodoSetting === false || this.isLocationFiltered === false) return;

      this.isLoadingCount = true;
      this.$axios
        .get(`/businesses/${this.organization.id}/todo/location-user-assigned/count`)
        .then((res) => {
          this.todoCount = res.data.count;
        })
        .catch((err) => {
          if (err && err.response && err.response.status === 401) {
            console.warn("Skipping todo count — unauthorized");
          } else {
            console.error("Todo count failed", err);
          }
        })
        .finally(() => {
          this.isLoadingCount = false;
        });
    },
    activation() {
      this.$router.push(`/activation/${this.organization.id}`);
    },
    async checkForBusinessPins() {
      try {
        if (!this.isOrganizationBusiness) return;
        const response = await this.$axios.get(`/business/${this.organization.id}/has-pins`);
        this.businessHasPins = response.data.hasPins;
      } catch {
        this.businessHasPins = false;
      }
    },
    async enablePinMode() {
      sessionStorage.removeItem("pinMode");
      sessionStorage.setItem("hasStartedPinMode", true);
      await this.$axios.post(`/businesses/${this.organization.id}/enable-pin-mode`);
      this.$router.push("/pin-mode");
    },
  },
};
</script>

<style lang="scss">
.nav-bar {
  background-color: var(--navbar-bg) !important;
}
</style>
